// Localização: frontend/src/components/Financeiro/GraficoComparativo.jsx
// Gráfico Comparativo Moderno de Receitas vs Despesas

import React, { useState } from 'react';
import { 
    BarChart, 
    Bar, 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
} from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import { TrendingUp, TrendingDown, BarChart3, LineChart as LineChartIcon } from 'lucide-react';

/**
 * @component GraficoComparativo
 * @description Gráfico comparativo moderno de receitas vs despesas com toggle entre barras e linha
 * @param {Array} data - Dados do fluxo de caixa ou dados mensais
 * @param {boolean} isLoading - Estado de carregamento
 * @param {string} error - Mensagem de erro
 */
const GraficoComparativo = ({ data = [], isLoading = false, error = null }) => {
    const [viewType, setViewType] = useState('barras'); // 'barras' ou 'linha'

    if (isLoading) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas vs Despesas</h3>
                    </div>
                    <div className="py-12">
                        <LoadingIndicator message="Carregando dados comparativos..." />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas vs Despesas</h3>
                    </div>
                    <div className="py-4">
                        <Alert type="error" message={error} />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-6 h-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas vs Despesas</h3>
                    </div>
                    <div className="h-96 flex flex-col items-center justify-center py-8">
                        <BarChart3 className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg">Sem dados para exibir</p>
                        <p className="text-gray-400 text-sm mt-2">Não há dados de receitas e despesas no período selecionado</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Formatar dados para o gráfico
    const chartData = data.map(item => ({
        periodo: item.periodo || item.mes || 'Período',
        receitas: parseFloat(item.receitas) || 0,
        despesas: parseFloat(item.despesas) || 0,
        saldo: parseFloat(item.saldo) || 0
    }));

    // Calcular totais
    const totalReceitas = chartData.reduce((sum, item) => sum + item.receitas, 0);
    const totalDespesas = chartData.reduce((sum, item) => sum + item.despesas, 0);
    const saldoTotal = totalReceitas - totalDespesas;

    // Tooltip customizado moderno
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const receitas = payload.find(p => p.dataKey === 'receitas')?.value || 0;
            const despesas = payload.find(p => p.dataKey === 'despesas')?.value || 0;
            const saldo = receitas - despesas;

            return (
                <div className="bg-white p-4 border border-gray-200 shadow-xl rounded-lg text-sm backdrop-blur-sm">
                    <p className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">{label}</p>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                <span className="text-gray-600">Receitas:</span>
                            </div>
                            <span className="font-semibold text-emerald-600">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(receitas)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <span className="text-gray-600">Despesas:</span>
                            </div>
                            <span className="font-semibold text-red-600">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(despesas)}
                            </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-gray-200">
                            <div className="flex items-center justify-between gap-4">
                                <span className="font-semibold text-gray-700">Saldo:</span>
                                <span className={`font-bold ${
                                    saldo >= 0 ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                    {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                    }).format(saldo)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
            <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                {/* Header com Toggle */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                            <BarChart3 className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">Receitas vs Despesas</h3>
                            <p className="text-sm text-gray-500">
                                Comparação entre receitas e despesas por período
                            </p>
                        </div>
                    </div>
                    
                    {/* Toggle de Visualização */}
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setViewType('barras')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'barras'
                                    ? 'bg-white text-indigo-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <BarChart3 className="w-4 h-4" />
                            Barras
                        </button>
                        <button
                            onClick={() => setViewType('linha')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'linha'
                                    ? 'bg-white text-indigo-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <LineChartIcon className="w-4 h-4" />
                            Linha
                        </button>
                    </div>
                </div>

                {/* Cards de Resumo */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-emerald-600" />
                            <p className="text-xs font-semibold text-emerald-700">Total Receitas</p>
                        </div>
                        <p className="text-xl font-bold text-emerald-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalReceitas)}
                        </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingDown className="w-4 h-4 text-red-600" />
                            <p className="text-xs font-semibold text-red-700">Total Despesas</p>
                        </div>
                        <p className="text-xl font-bold text-red-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalDespesas)}
                        </p>
                    </div>
                    <div className={`bg-gradient-to-br rounded-lg p-4 border ${
                        saldoTotal >= 0
                            ? 'from-emerald-50 to-emerald-100 border-emerald-200'
                            : 'from-red-50 to-red-100 border-red-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            {saldoTotal >= 0 ? (
                                <TrendingUp className="w-4 h-4 text-emerald-600" />
                            ) : (
                                <TrendingDown className="w-4 h-4 text-red-600" />
                            )}
                            <p className={`text-xs font-semibold ${
                                saldoTotal >= 0 ? 'text-emerald-700' : 'text-red-700'
                            }`}>
                                Saldo Total
                            </p>
                        </div>
                        <p className={`text-xl font-bold ${
                            saldoTotal >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(saldoTotal)}
                        </p>
                    </div>
                </div>

                {/* Gráfico */}
                <div className="outline-none flex-1" style={{ outline: 'none', marginBottom: '0px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {viewType === 'barras' ? (
                            <BarChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                                    </linearGradient>
                                    <linearGradient id="colorDespesas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#dc2626" stopOpacity={0.8} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={-30}
                                    textAnchor="end"
                                    height={80}
                                    tick={{ fill: '#6b7280' }}
                                    axisLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    iconType="circle"
                                />
                                <Bar 
                                    dataKey="receitas" 
                                    fill="url(#colorReceitas)" 
                                    name="Receitas"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                />
                                <Bar 
                                    dataKey="despesas" 
                                    fill="url(#colorDespesas)" 
                                    name="Despesas"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                />
                            </BarChart>
                        ) : (
                            <LineChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="gradientReceitas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradientDespesas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={-30}
                                    textAnchor="end"
                                    height={80}
                                    tick={{ fill: '#6b7280' }}
                                    axisLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    iconType="circle"
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="receitas" 
                                    stroke="#10b981" 
                                    strokeWidth={3}
                                    name="Receitas"
                                    dot={{ fill: '#10b981', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="despesas" 
                                    stroke="#ef4444" 
                                    strokeWidth={3}
                                    name="Despesas"
                                    dot={{ fill: '#ef4444', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default GraficoComparativo;
